import { useEffect, useState } from 'react';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { BrandCanvas } from './BrandCanvas';
import { LoginStage } from './stage/LoginStage';
import { StandardForm } from './StandardForm';
import type { LoginCredentials } from './StandardForm';
import { PortalSwitcher } from './PortalSwitcher';
import { ForgotPasswordModal } from './ForgotPasswordModal';
import { LegalModal } from './LegalModal';
import type { LegalDocKind } from './legalContent';
import { Notice } from './Notice';
import type { NoticeTone } from './Notice';
import { defaultPortalLinks } from './portalLinks';
import type { PortalLink } from './portalLinks';
import type { Portal } from './types';
import { getLoginKitMessages } from './i18n';
import type { Locale } from './i18n';

export type ResponsiveStrategy = 'mobile-first' | 'desktop-first';

/**
 * Which login to draw around the same form.
 *  - `classic` — the split screen with the portal's brand canvas (`login.css`).
 *  - `stage` — the full-bleed dark stage of the "site" design system, with the form in a glass card
 *    (`login-stage.css`, {@link LoginStage}). Same states, same handlers; only the scenery changes.
 */
export type LoginVariant = 'classic' | 'stage';

/**
 * Estado de bloqueio pós-autenticação (ex.: sessão válida mas sem a permission
 * do portal). Renderizado DENTRO da própria tela de login — nunca numa tela
 * separada — para preservar o padrão visual.
 */
export interface LoginBlocked {
  /** Título do bloco (ex.: nome do usuário ou "Acesso não autorizado"). */
  title?: string;
  /** Mensagem explicativa do bloqueio. */
  message: ReactNode;
  /**
   * Refaz a tentativa que falhou, sem encerrar a sessão. Renderizado como a ação primária.
   *
   * Existe porque nem todo bloqueio é definitivo: uma falha de rede ao carregar as permissões produz
   * exatamente a mesma tela de "você não tem acesso", e sair era a única saída oferecida para algo que
   * um segundo pedido normalmente resolve.
   */
  onRetry?: () => void | Promise<void>;
  /** Rótulo do botão de tentar de novo. Default: "Tentar de novo". */
  retryLabel?: string;
  /** Encerra a sessão atual (para o usuário tentar outra conta). */
  onSignOut?: () => void | Promise<void>;
  /** Rótulo do botão de sair. Default: "Sair". */
  signOutLabel?: string;
}

export interface LoginPageProps {
  /** Portal a renderizar. Cada app passa o seu. */
  portal: Portal;
  /**
   * Estratégia responsiva. `mobile-first` (Paciente, Empresa) parte do empilhado
   * e expande para split em ≥900px; `desktop-first` (Cobrança, Admin) parte do
   * split e degrada para empilhado em ≤900px. Default: `desktop-first`.
   */
  responsive?: ResponsiveStrategy;
  /** Login drawn around the form. Default: `classic`. Requires `@tmr/login-kit/login-stage.css` for `stage`. */
  variant?: LoginVariant;
  /** Lado do canvas da marca. Default: `left`. */
  brandSide?: 'left' | 'right';
  /** Liga/desliga as animações de assinatura. Default: `true`. */
  motion?: boolean;
  /**
   * Sessão sendo verificada: mostra um loader on-brand no lugar do formulário,
   * mantendo o mesmo split-screen do login. Use enquanto a autenticação resolve,
   * em vez de uma tela de carregamento separada.
   */
  busy?: boolean;
  /** Texto opcional abaixo do loader quando `busy`. Ex.: "Verificando sessão". */
  busyLabel?: string;
  /**
   * Sessão autenticada, mas bloqueada para este portal (erro de autorização).
   * Substitui o formulário por um aviso on-brand + ação de sair, mantendo a
   * mesma tela de login. Use em vez de uma tela de "acesso negado" separada.
   */
  blocked?: LoginBlocked;
  /**
   * Aviso já visível quando a tela abre — para quem chega ao login vindo de outro lugar, com algo por
   * explicar. Diferente de {@link blocked}: ali não há o que fazer senão sair, aqui o formulário continua
   * inteiro, que é o certo para quem só precisa tentar de novo.
   */
  notice?: { tone: NoticeTone; message: string } | null;
  /** Handler de login e-mail+senha. */
  onSubmit?: (creds: LoginCredentials) => Promise<void> | void;
  /** Handler de link mágico. */
  onMagicLink?: (identifier: string, onOutcome?: (error: Error) => void) => Promise<void> | void;
  /**
   * Handler de SSO. Mantido na API para compatibilidade com os apps, mas SEM
   * efeito por ora — os provedores de identidade foram removidos da tela até que
   * o produto os habilite de novo (ver {@link StandardForm}).
   */
  onSso?: (provider: 'google' | 'apple' | 'microsoft') => void;
  /** Handler de recuperação de senha — passo 1 (envia o código). */
  onResetPassword?: (email: string) => Promise<void> | void;
  /** Handler de recuperação de senha — passo 2 (verifica o código + nova senha). */
  onResetVerify?: (params: { code: string; password: string }) => Promise<void> | void;
  /** Exibe o seletor de portais ("Portais TMR"). Default: `true`. */
  showSwitcher?: boolean;
  /** Exibe o seletor de idioma. Default: `true`. */
  showLocaleSwitcher?: boolean;
  /** Idioma da interface (pt-BR, en, es). Default: `pt-BR`. */
  locale?: Locale;
  /** Callback disparado quando o idioma muda no seletor. */
  onLocaleChange?: (locale: Locale) => void;
  /** Chave usada para persistencia de idioma no localStorage. */
  localeStorageKey?: string;
  /**
   * Links do seletor de portais. Por padrão, monta a partir dos dados
   * compartilhados + envs `VITE_PORTAL_URL_*` (fallback localhost em dev).
   */
  portalLinks?: PortalLink[];
  /** Rodapé legal — editável por app. Default: Termos + Política. */
  legal?: ReactNode;
}

/**
 * Página de login completa (padrão TMR) — split screen com canvas da marca + o
 * formulário padrão compartilhado, seletor de portais e modal de recuperação de
 * senha. COMPARTILHADA entre todos os portais; cada app fornece o seu `portal`
 * e pode sobrepor cópia/CSS na sua camada isolada.
 */
export function LoginPage({
  portal,
  responsive = 'desktop-first',
  variant = 'classic',
  brandSide = 'left',
  motion = true,
  busy = false,
  busyLabel,
  blocked,
  notice = null,
  onSubmit,
  onMagicLink,
  onResetPassword,
  onResetVerify,
  showSwitcher = true,
  showLocaleSwitcher = true,
  locale,
  onLocaleChange,
  localeStorageKey = 'tmr.login.locale',
  portalLinks,
  legal,
}: LoginPageProps) {
  const isLocale = (value: unknown): value is Locale => value === 'pt-BR' || value === 'en' || value === 'es';
  const readStoredLocale = (): Locale | null => {
    if (typeof window === 'undefined') return null;
    const stored = window.localStorage.getItem(localeStorageKey);
    return isLocale(stored) ? stored : null;
  };

  const brandLeft = brandSide !== 'right';
  const [forgotOpen, setForgotOpen] = useState(false);
  const [legalDoc, setLegalDoc] = useState<LegalDocKind | null>(null);
  const [localeMenuOpen, setLocaleMenuOpen] = useState(false);
  const [localLocale, setLocalLocale] = useState<Locale>(() => locale ?? readStoredLocale() ?? 'pt-BR');
  const effectiveLocale = locale ?? localLocale;
  const links = portalLinks ?? defaultPortalLinks();
  const t = getLoginKitMessages(effectiveLocale);
  const localeItems = [
    { value: 'pt-BR', label: 'PT', flag: '🇧🇷', title: 'Português (Brasil)' },
    { value: 'en', label: 'EN', flag: '🇺🇸', title: 'English' },
    { value: 'es', label: 'ES', flag: '🇪🇸', title: 'Español' },
  ] as const;
  const activeLocaleItem = localeItems.find((item) => item.value === effectiveLocale) ?? localeItems[0];

  useEffect(() => {
    if (locale) setLocalLocale(locale);
  }, [locale]);

  const changeLocale = (next: Locale) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(localeStorageKey, next);
    }
    if (!locale) setLocalLocale(next);
    onLocaleChange?.(next);
    setLocaleMenuOpen(false);
  };

  const openLegal = (doc: LegalDocKind) => (e: MouseEvent) => {
    e.preventDefault();
    setLegalDoc(doc);
  };

  const defaultLegal = (
    <>
      {t.legal.prefix}{' '}
      <a href="#" onClick={openLegal('terms')}>
        {t.legal.terms}
      </a>{' '}
      {t.legal.middle}{' '}
      <a href="#" onClick={openLegal('privacy')}>
        {t.legal.privacy}
      </a>
      {t.legal.suffix}
    </>
  );

  return (
    <div
      className={
        variant === 'stage'
          ? 'login-shell login-shell--stage'
          : `login-shell login-shell--${responsive} ${brandLeft ? 'brand-left' : 'brand-right'}`
      }
      data-portal={portal.id}
    >
      {variant === 'stage' ? (
        <LoginStage portal={portal} motionOn={motion} locale={effectiveLocale} />
      ) : (
        <BrandCanvas portal={portal} motionOn={motion} locale={effectiveLocale} />
      )}

      <div className="form-pane">
        {showLocaleSwitcher && (
          <div className={`locale-switcher ${localeMenuOpen ? 'is-open' : ''}`} role="group" aria-label={t.switcher.language}>
            {localeMenuOpen && (
              <div className="locale-flyout" role="menu" aria-label={t.switcher.language}>
                {localeItems.filter((item) => item.value !== effectiveLocale).map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    className="locale-btn"
                    onClick={() => changeLocale(item.value)}
                    aria-label={item.title}
                    title={item.title}
                    role="menuitemradio"
                    aria-checked={false}
                  >
                    <span className="locale-flag" aria-hidden="true">{item.flag}</span>
                    <span className="locale-code">{item.label}</span>
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              className="locale-btn locale-trigger is-active"
              onClick={() => setLocaleMenuOpen(!localeMenuOpen)}
              aria-haspopup="menu"
              aria-expanded={localeMenuOpen}
              aria-label={activeLocaleItem.title}
              title={activeLocaleItem.title}
            >
              <span className="locale-flag" aria-hidden="true">{activeLocaleItem.flag}</span>
              <span className="locale-code">{activeLocaleItem.label}</span>
            </button>
          </div>
        )}

        {showSwitcher && (
          <PortalSwitcher
            currentId={portal.id}
            links={links}
            labels={{
              trigger: t.switcher.trigger,
              head: t.switcher.head,
              current: t.switcher.current,
            }}
          />
        )}

        <div className="form-wrap">
          {busy ? (
            <div className="form-loading" role="status" aria-live="polite" style={{ '--accent': portal.accent } as CSSProperties}>
              <span className="form-loading__spinner" aria-hidden="true" />
              {busyLabel && <span className="form-loading__label">{busyLabel}</span>}
            </div>
          ) : blocked ? (
            <div className="login-form" style={{ '--accent': portal.accent, '--accent-soft': portal.accentSoft } as CSSProperties}>
              <div className="form-chip">
                <span className="chip-dot" />
                {portal.name}
              </div>
              {blocked.title && <h2 className="form-title">{blocked.title}</h2>}
              <Notice tone="error" title={t.notice.errorTitle}>
                {blocked.message}
              </Notice>
              {blocked.onRetry && (
                <button type="button" className="primary-btn" onClick={() => void blocked.onRetry?.()}>
                  {blocked.retryLabel ?? t.form.tryAgain}
                </button>
              )}
              {blocked.onSignOut && (
                <button type="button" className="ghost-btn" onClick={() => void blocked.onSignOut?.()}>
                  {blocked.signOutLabel ?? t.form.backToLogin}
                </button>
              )}
            </div>
          ) : (
            <StandardForm
              portal={portal}
              locale={effectiveLocale}
              onSubmit={onSubmit}
              onMagicLink={onMagicLink}
              onForgot={() => setForgotOpen(true)}
              initialNotice={notice}
            />
          )}
        </div>
        <div className="form-legal">{legal ?? defaultLegal}</div>
      </div>

      {forgotOpen && (
        <ForgotPasswordModal
          portal={portal}
          locale={effectiveLocale}
          onReset={onResetPassword}
          onResetVerify={onResetVerify}
          onClose={() => setForgotOpen(false)}
        />
      )}

      {legalDoc && (
        <LegalModal
          portal={portal}
          locale={effectiveLocale}
          initial={legalDoc}
          onClose={() => setLegalDoc(null)}
        />
      )}
    </div>
  );
}
